import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { useActiveCoachmark } from "contexts/coachmarks";
import { selectObjectMeasureIds } from "features/measure/measureSlice";
import { Picker, selectPicker } from "features/render";
import { selectIsOnline, selectIsProcoreMobileDesign } from "slices/explorer";

import { Coachmark } from "./coachmark";
import { getCoachmarkTop } from "./coachmarkLayout";
import { AnchoredPickPrompt, CursorPickPrompt } from "./pickPrompt";

const PICKERS = [
    Picker.OutlineLaser,
    Picker.Coordinates,
    Picker.PointLine,
    Picker.Dimension,
    Picker.ObjectMeasurement,
    Picker.SectionPlane,
    Picker.Pins,
];

/** Gap between the coachmark and the prompt stacked under it, on touch. */
const PROMPT_GAP_PX = 8;

export function CoachmarkDisplay({ topOffset }: { topOffset?: string } = {}) {
    const { t } = useTranslation();

    const customCoachmark = useActiveCoachmark();

    const globalPicker = useAppSelector(selectPicker);
    const pickerRef = useRef(globalPicker);
    if (PICKERS.includes(globalPicker)) {
        pickerRef.current = globalPicker;
    }
    const picker = pickerRef.current;

    const pickPrompt = usePickPrompt(globalPicker, topOffset);

    if (customCoachmark) {
        return (
            <Coachmark
                key={customCoachmark.id}
                label={customCoachmark.label}
                description={customCoachmark.tooltipMessage}
                buttonLabel={customCoachmark.buttonLabel}
                onButtonClick={customCoachmark.buttonOnClick}
                visible={true}
                topOffset={topOffset}
            />
        );
    }

    let label = "";
    let description = "";
    switch (picker) {
        case Picker.OutlineLaser:
            label = t("laser");
            description = t("laserHowTo");
            break;
        case Picker.Coordinates:
            label = t("coordinates");
            description = t("coordinatesHowTo");
            break;
        case Picker.PointLine:
            label = t("pointToPoint");
            description = t("pointToPointHowTo");
            break;
        case Picker.Dimension:
            label = t("dimension");
            description = t("dimensionHowTo");
            break;
        case Picker.ObjectMeasurement:
            label = t("objectMeasurement");
            description = t("objectMeasurementHowTo");
            break;
        case Picker.Pins:
            label = t("pins");
            description = t("pinsHowTo");
            break;
        case Picker.SectionPlane:
            label = t("sectionPlane");
            description = t("sectionPlaneHowTo");
            break;
    }

    const visible = PICKERS.includes(globalPicker);

    return (
        <>
            <Coachmark
                key={picker}
                ref={pickPrompt.coachmarkRef}
                label={label}
                description={description}
                visible={visible}
                topOffset={topOffset}
            />
            {pickPrompt.element}
        </>
    );
}

/**
 * The running instruction for the object measurement tool, which needs one object picked and then a
 * second. It trails the cursor on desktop, and stacks under the coachmark on touch, where there is
 * no cursor to trail. Null while the tool is not mid-pick.
 */
function usePickPrompt(picker: Picker, topOffset: string | undefined) {
    const { t } = useTranslation();
    const isMobile = useAppSelector(selectIsProcoreMobileDesign);
    const isOnline = useAppSelector(selectIsOnline);
    const objectIds = useAppSelector(selectObjectMeasureIds);

    const coachmarkRef = useRef<HTMLDivElement | null>(null);
    const [coachmarkHeight, setCoachmarkHeight] = useState(0);

    const visible = picker === Picker.ObjectMeasurement && objectIds.length < 2;

    // The coachmark grows when expanded, so measure it rather than assume a height and end up with
    // the prompt underneath it.
    useLayoutEffect(() => {
        const element = coachmarkRef.current;
        if (!element || !isMobile || !visible) {
            return;
        }

        const observer = new ResizeObserver(() => setCoachmarkHeight(element.offsetHeight));
        observer.observe(element);
        setCoachmarkHeight(element.offsetHeight);

        return () => observer.disconnect();
    }, [isMobile, visible]);

    if (!visible) {
        return { coachmarkRef, element: null };
    }

    const text = objectIds.length === 0 ? t("selectFirstObject") : t("selectSecondObject");

    return {
        coachmarkRef,
        element: isMobile ? (
            <AnchoredPickPrompt
                text={text}
                top={`calc(${getCoachmarkTop(topOffset, isOnline)} + ${coachmarkHeight + PROMPT_GAP_PX}px)`}
            />
        ) : (
            <CursorPickPrompt text={text} />
        ),
    };
}
